/**
 * TreemapView — Visual treemap of filesystem entries
 *
 * Renders rectangles sized proportionally to file/folder sizes.
 * Click a directory to drill in, click a file to select it.
 *
 * @module @humanizer/studio/components/import/TreemapView
 */

import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { computeTreemap, getFileTypeColor, type TreemapRect } from '../../utils/treemap';
import type { DirEntryWithStats } from '../../types/electron';

interface TreemapViewProps {
  entries: DirEntryWithStats[];
  dirSizes: Map<string, number>;
  selectedPaths: Set<string>;
  onNavigate: (path: string) => void;
  onToggleSelect: (path: string) => void;
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '...';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function TreemapView({
  entries,
  dirSizes,
  selectedPaths,
  onNavigate,
  onToggleSelect,
}: TreemapViewProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rects = useMemo(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return [];
    const items = entries.map(entry => ({
      name: entry.name,
      size: entry.isDirectory ? (dirSizes.get(entry.path) || 0) : entry.size,
      isDirectory: entry.isDirectory,
      path: entry.path,
      mtime: entry.mtime,
    }));
    return computeTreemap(items, dimensions.width, dimensions.height);
  }, [entries, dirSizes, dimensions]);

  const handleClick = (rect: TreemapRect) => {
    if (rect.isDirectory) {
      onNavigate(rect.path);
    } else {
      onToggleSelect(rect.path);
    }
  };

  // Min size to show label text
  const MIN_LABEL_W = 50;
  const MIN_LABEL_H = 28;

  return (
    <div className="treemap-container" ref={containerRef}>
      {rects.map((rect) => {
        const isSelected = selectedPaths.has(rect.path);
        const isHovered = hoveredPath === rect.path;
        const showLabel = rect.w >= MIN_LABEL_W && rect.h >= MIN_LABEL_H;
        const showSize = rect.w >= 80 && rect.h >= 44;
        const colorClass = getFileTypeColor(rect.name, rect.isDirectory);

        return (
          <div
            key={rect.path}
            className={`treemap-rect ${colorClass} ${isSelected ? 'treemap-rect--selected' : ''} ${isHovered ? 'treemap-rect--hovered' : ''}`}
            style={{
              left: rect.x,
              top: rect.y,
              width: rect.w,
              height: rect.h,
            }}
            onClick={() => handleClick(rect)}
            onMouseEnter={() => setHoveredPath(rect.path)}
            onMouseLeave={() => setHoveredPath(null)}
            title={`${rect.name}\n${formatSize(rect.size)}\n${rect.mtime ? new Date(rect.mtime).toLocaleDateString() : ''}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleClick(rect);
            }}
          >
            {showLabel && (
              <span className="treemap-rect__label">
                {rect.isDirectory ? `${rect.name}/` : rect.name}
              </span>
            )}
            {showSize && (
              <span className="treemap-rect__size">
                {formatSize(rect.size)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default TreemapView;
